<!--
  PricingPanel -- the Pricing tab of the product editor.

  Rebuilt 2026-09-29 around TWO pricing modes (Jake):

    - FIXED PRICE: the distributor types the exact price for each selected
      size. Persisted as `fixed_size_prices` ({size: integer cents}). No
      margin, no store grid.
    - STORE PRICING: the price comes from the store's pricing tables. This
      panel shows a READ-ONLY per-size preview from the server's
      `price_preview` ({size: cents}, quoted at quantity 1) and links to the
      store Pricing page, which is where those tables are edited.

  The mode is `pricing_mode` ("fixed" | "store") on the campaign product.

  PER-SIZE BREAKDOWN (Jake, 2026-10-06). Both modes show what each size
  costs. Store pricing shows, per size, blank cost and charge, decoration
  cost and charge, the store price, and margin AND markup separately for
  blank goods and for decoration, at one quantity break of the store's
  pricing at a time; a stepper walks the breaks and shows the quantity range
  the numbers apply to. Fixed price shows blank, decoration and total cost
  (for one item) and one margin/markup pair of the fixed price against the
  total -- a fixed price cannot be split between the two -- with a flag when
  it is below cost, and no stepper (a fixed price does not vary by
  quantity). Source: `store.priceBreaks`, from
  GET /campaign_product/<id>/price_breaks (server `price_for.price_breaks`).

  EMBROIDERY STITCH COUNT (Jake, 2026-10-08). Embroidery decoration cost is
  priced off a stitch count: the digitized ACTUAL, else a Wilcom ESTIMATE
  bought by the server's estimate drain, else (Jake, 2026-10-22) an estimate
  from the design's SIZE (source 'size_estimate'), so it is never $0.00 for
  want of a count. A note says which (with the count) and, for a size
  estimate, what replaces it. Source: `priceBreaks.stitch_count`.

  MISSING SUPPLIER COST (Jake, 2026-10-06). When the supplier gave no blank
  cost for a selected size, an alert at the top of the panel names those
  sizes, says what each is costed from now (the distributor's entered cost,
  the product's base cost, or nothing -- no price), and takes a per-size
  cost. The server remembers it per distributor + supplier + style + size
  (`manual_blank_costs`), so every product of the distributor using the
  style picks it up; `price_for` reads supplier cost, then that, then the
  base cost. When the supplier later prices such a size, a notice shows its
  cost beside the one entered until "Got it". Source: `costs[size]` of the
  same price_breaks payload.

  NO CLIENT-SIDE PRICE MATH. Every number shown is a server-quoted cent value
  (`price_preview`, `price_breaks`) or one the distributor typed
  (`fixed_size_prices`). The ONE exception is the fixed-price margin/markup:
  a ratio of the typed price over the server-quoted total cost, computed in
  `priceBreakdown.fixedPriceRatios` so it follows the typing live. It is a
  ratio, never a price. The
  margin controls this panel used to carry (Gross Margin %, Upcharge $, Reset,
  the default_margin row, Apply suggested retail) are gone: they no longer
  feed the price, and the old client-side `cost * (1 + margin)` "Store Price"
  disagreed with the charge (the $5.18 shown vs $6.17 charged bug).

  Switching modes never silently changes a price:
    - store -> fixed seeds each size from the current preview (or a
      previously saved fixed price), so the product keeps charging what it
      charged a moment ago. If a size has no price to seed from, the switch
      is HELD locally until the distributor prices it -- the contract rejects
      "fixed" without a price for every selected size.
    - fixed -> store asks first, and keeps `fixed_size_prices` so switching
      back restores them.

  Cross-tab contract (unchanged): reads through
  `store.getEffectiveAttribute(path)`, writes through
  `store.updateField('<top-level-attr>', value)`. `selected_sizes` is owned by
  the Sizes & Colors tab and read here through the same getter.
-->
<template>
    <v-card data-testid="pricing-card">
        <v-card-title class="pricing-header">
            <v-icon icon="sell" class="pricing-header-icon" />
            <div class="pricing-header-text">
                <div class="pricing-title">Pricing</div>
                <div class="pricing-subtitle">
                    Choose how this product is priced in the store
                </div>
            </div>
            <PricingModeBadge :mode="savedMode" />
        </v-card-title>

        <v-card-text>
            <!-- Sizes the supplier gave no blank cost for (Jake, 2026-10-06).
                 Two groups, one row template:
                   - WARNING (orange, role=alert, at the top so it is not
                     missed): sizes still costed from the product's base
                     cost or with no cost at all.
                   - IN USE (calm, role=status): sizes priced from the cost
                     the distributor entered -- nothing is wrong, but the
                     supplier still has no cost, so it stays visible and
                     editable.
                 Only an account owner (or superadmin) may enter a cost:
                 it prices every product in every store using the style. -->
            <!-- Selected sizes the supplier does not offer (Jake, 2026-10-07):
                 flagged with a one-click remove, and kept out of the
                 missing-cost prompt below -- a blank cost for a size nobody
                 can buy means nothing. -->
            <section
                v-if="unavailableSizes.length"
                class="cost-alert cost-alert--warning"
                role="alert"
                data-testid="pricing-unavailable"
            >
                <p class="cost-alert__title">
                    Some selected sizes are not made by {{ supplierName }}.
                </p>
                <ul class="unavailable-list">
                    <li
                        v-for="size in unavailableSizes"
                        :key="size"
                        class="unavailable-list__row"
                        :data-testid="`pricing-unavailable-${size}`"
                    >
                        <strong>{{ size }}</strong>
                        <span>Not available from {{ supplierName }}</span>
                        <button
                            type="button"
                            class="pricing-btn"
                            :data-testid="`pricing-unavailable-remove-${size}`"
                            :aria-label="`Remove size ${size}`"
                            @click="store.deselectSize(size)"
                        >
                            Remove
                        </button>
                    </li>
                </ul>
            </section>
            <section
                v-for="group in missingCostGroups"
                :key="group.kind"
                :class="['cost-alert', group.kind === 'warning' ? 'cost-alert--warning' : 'cost-alert--calm']"
                :role="group.kind === 'warning' ? 'alert' : 'status'"
                :data-testid="group.kind === 'warning' ? 'pricing-missing-cost-warning' : 'pricing-manual-cost-in-use'"
            >
                <template v-if="group.kind === 'warning'">
                    <p class="cost-alert__title" data-testid="pricing-missing-cost-sizes">
                        The supplier has no blank cost for
                        {{ group.sizes.join(', ') }}.
                    </p>
                    <p class="cost-alert__copy">
                        Until a cost is entered, these sizes are priced from
                        the product's base blank cost (often too low) or have
                        no price. A cost entered here applies to every product
                        in your stores that uses this style; the supplier's own
                        cost replaces it once the supplier provides one.
                    </p>
                </template>
                <p v-else class="cost-alert__title" data-testid="pricing-manual-cost-in-use-sizes">
                    Using your blank cost for {{ group.sizes.join(', ') }}
                    (the supplier has none).
                </p>
                <p
                    v-if="!manualCostAvailable"
                    class="cost-alert__copy"
                    data-testid="pricing-manual-cost-unavailable"
                >
                    This product has no supplier style yet, so a cost cannot
                    be remembered for it. Choose its supplier and SKU first.
                </p>
                <p
                    v-else-if="!manualCostEditable"
                    class="cost-alert__copy"
                    data-testid="pricing-manual-cost-owner-only"
                >
                    Only an account owner can enter or change a blank cost,
                    because it applies to every store.
                </p>
                <table class="cost-alert__table">
                    <thead>
                        <tr>
                            <th>Size</th>
                            <th>Cost used now</th>
                            <th v-if="manualCostEditable">Your blank cost</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="size in group.sizes" :key="size">
                            <td>{{ size }}</td>
                            <td
                                :data-testid="`pricing-missing-cost-used-${size}`"
                                :data-cost-source="costs[size]?.blank_cost_source ?? 'none'"
                            >
                                {{ costUsedLabel(size) }}
                            </td>
                            <td v-if="manualCostEditable">
                                <div class="same-price-controls">
                                    <div class="currency-field">
                                        <span class="currency-prefix">$</span>
                                        <input
                                            type="text"
                                            inputmode="decimal"
                                            class="price-input"
                                            :data-testid="`pricing-manual-cost-input-${size}`"
                                            :aria-label="`Blank cost for size ${size}`"
                                            :value="manualCostInputValue(size)"
                                            @input="onManualCostInput(size, $event)"
                                            @keydown.enter.prevent="saveManualCost(size)"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        class="pricing-btn"
                                        :data-testid="`pricing-manual-cost-save-${size}`"
                                        :disabled="manualCostSaving[size] === true"
                                        @click="saveManualCost(size)"
                                    >
                                        Save cost
                                    </button>
                                </div>
                                <p
                                    v-if="manualCostSaved[size]"
                                    class="cost-alert__saved"
                                    role="status"
                                    :data-testid="`pricing-manual-cost-saved-${size}`"
                                >
                                    Saved -- used for {{ size }} in every store with this style.
                                </p>
                                <p
                                    v-if="manualCostErrors[size]"
                                    class="pricing-error"
                                    :data-testid="`pricing-manual-cost-error-${size}`"
                                >
                                    {{ manualCostErrors[size] }}
                                </p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>
            <!-- The supplier now prices a size the distributor had entered a
                 cost for: its cost is used from now on. Shown, with the old
                 manual cost beside it, until "Got it" (which only records
                 that it was seen -- the manual cost is kept). -->
            <section
                v-if="takeoverSizes.length"
                class="cost-alert cost-alert--info"
                role="status"
                data-testid="pricing-supplier-took-over"
            >
                <p class="cost-alert__title">
                    The supplier now has a blank cost for
                    {{ takeoverSizes.join(', ') }}, and it replaces the cost
                    you entered.
                </p>
                <table class="cost-alert__table">
                    <thead>
                        <tr>
                            <th>Size</th>
                            <th>Supplier cost (used now)</th>
                            <th>You had entered</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="size in takeoverSizes" :key="size">
                            <td>{{ size }}</td>
                            <td :data-testid="`pricing-takeover-supplier-${size}`">{{ money(costs[size].supplier_cost_cents) }}</td>
                            <td :data-testid="`pricing-takeover-manual-${size}`">{{ money(costs[size].manual_cost_cents) }}</td>
                        </tr>
                    </tbody>
                </table>
                <button
                    v-if="manualCostEditable"
                    type="button"
                    class="pricing-btn"
                    data-testid="pricing-takeover-dismiss"
                    @click="dismissTakeover"
                >
                    Got it
                </button>
                <p
                    v-if="takeoverError"
                    class="pricing-error"
                    data-testid="pricing-takeover-error"
                >
                    {{ takeoverError }}
                </p>
            </section>

            <div
                class="mode-control"
                role="group"
                aria-label="Pricing mode"
                data-testid="pricing-mode-control"
            >
                <button
                    type="button"
                    data-testid="pricing-mode-fixed"
                    :class="['mode-choice', { 'mode-choice--active': displayMode === PRICING_MODE_FIXED }]"
                    :aria-pressed="displayMode === PRICING_MODE_FIXED ? 'true' : 'false'"
                    @click="chooseFixed"
                >
                    <span class="mode-choice__title">Fixed price</span>
                    <span class="mode-choice__copy">
                        You set the exact price for each size.
                    </span>
                </button>
                <button
                    type="button"
                    data-testid="pricing-mode-store"
                    :class="['mode-choice', { 'mode-choice--active': displayMode === PRICING_MODE_STORE }]"
                    :aria-pressed="displayMode === PRICING_MODE_STORE ? 'true' : 'false'"
                    @click="chooseStore"
                >
                    <span class="mode-choice__title">Store pricing</span>
                    <span class="mode-choice__copy">
                        Priced by this store's pricing tables.
                    </span>
                </button>
            </div>

            <p
                v-if="sizes.length === 0"
                class="pricing-hint"
                data-testid="pricing-no-sizes"
            >
                Select sizes on the Sizes &amp; Colors tab to see and set prices.
            </p>

            <p
                v-if="priceBreaksFailed && sizes.length"
                class="pricing-warning"
                data-testid="pricing-costs-unavailable"
            >
                Costs and margins could not be loaded right now. Prices are
                unaffected; reopen this tab to try again.
            </p>

            <p
                v-if="stitchCount && sizes.length"
                :class="stitchCount.status === 'ready' ? 'pricing-hint' : 'pricing-warning'"
                :data-source="stitchCount.source ?? ''"
                :data-status="stitchCount.status"
                data-testid="pricing-stitch-count"
            >
                <template v-if="stitchCount.source === 'size_estimate'">
                    Embroidery decoration cost is priced at about
                    {{ formatStitches(stitchCount.count) }} stitches. At least one
                    placement has no stitch count recorded yet, so its cost is
                    estimated from the design's size.
                    <template v-if="stitchCount.status === 'pending'">
                        It updates once the design's stitch count is estimated or digitized.
                    </template>
                    <template v-else>
                        It updates once the design is digitized.
                    </template>
                </template>
                <template v-else-if="stitchCount.status === 'ready' && stitchCount.source === 'actual' && stitchCount.from_file">
                    Embroidery decoration cost uses the stitch count from your
                    uploaded stitch file ({{ formatStitches(stitchCount.count) }} stitches).
                </template>
                <template v-else-if="stitchCount.status === 'ready' && stitchCount.source === 'actual'">
                    Embroidery decoration cost uses the digitized design's
                    stitch count ({{ formatStitches(stitchCount.count) }} stitches).
                </template>
                <template v-else-if="stitchCount.status === 'ready'">
                    Embroidery decoration cost is an estimate, based on about
                    {{ formatStitches(stitchCount.count) }} stitches. It updates
                    to the actual stitch count once the design is digitized.
                </template>
                <template v-else-if="stitchCount.count != null">
                    Embroidery decoration cost is based on about
                    {{ formatStitches(stitchCount.count) }} stitches, but
                    another embroidered placement on this product has no
                    stitch count, so the cost may be too low.
                </template>
                <template v-else-if="stitchCount.status === 'pending'">
                    The embroidery stitch count has not been estimated yet, so
                    decoration cost shows $0.00 until it is.
                </template>
                <template v-else-if="stitchCount.status === 'off'">
                    Automatic stitch-count estimates are not available right
                    now, so embroidery decoration cost shows $0.00.
                </template>
                <template v-else>
                    We could not estimate a stitch count for this design, so
                    embroidery decoration cost shows $0.00 until it is digitized.
                </template>
            </p>

            <!-- FIXED PRICE ------------------------------------------------>
            <section
                v-if="displayMode === PRICING_MODE_FIXED"
                class="pricing-section"
                data-testid="pricing-fixed-section"
            >
                <p
                    v-if="pendingFixed"
                    class="pricing-warning"
                    data-testid="pricing-fixed-pending"
                >
                    Enter a price for every size to switch this product to a
                    fixed price. It stays on store pricing until then.
                </p>

                <div v-if="sizes.length" class="same-price-row">
                    <label class="section-label" for="pricing-fixed-all">
                        Same price for all sizes
                    </label>
                    <div class="same-price-controls">
                        <div class="currency-field">
                            <span class="currency-prefix">$</span>
                            <input
                                id="pricing-fixed-all"
                                v-model="allSizesDraft"
                                type="text"
                                inputmode="decimal"
                                class="price-input"
                                data-testid="pricing-fixed-all-input"
                                aria-label="Price for all sizes"
                                @keydown.enter.prevent="applyAllSizes"
                            />
                        </div>
                        <button
                            type="button"
                            class="pricing-btn"
                            data-testid="pricing-fixed-all-apply"
                            @click="applyAllSizes"
                        >
                            Apply to all sizes
                        </button>
                    </div>
                    <p
                        v-if="allSizesError"
                        class="pricing-error"
                        data-testid="pricing-fixed-all-error"
                    >
                        {{ allSizesError }}
                    </p>
                </div>

                <div v-if="sizes.length" class="price-table-scroll">
                <table class="price-table">
                    <thead>
                        <tr>
                            <th>Size</th>
                            <th>Price</th>
                            <th>Cost <span class="th-sub">blank + decoration</span></th>
                            <th>Margin <span class="th-sub">markup</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="size in sizes"
                            :key="size"
                            :data-testid="`pricing-fixed-row-${size}`"
                        >
                            <td>{{ size }}</td>
                            <td>
                                <div class="currency-field">
                                    <span class="currency-prefix">$</span>
                                    <input
                                        type="text"
                                        inputmode="decimal"
                                        class="price-input"
                                        :data-testid="`pricing-fixed-price-${size}`"
                                        :aria-label="`Price for size ${size}`"
                                        :value="fixedInputValue(size)"
                                        @input="onFixedInput(size, $event)"
                                        @blur="onFixedBlur(size)"
                                        @keydown.enter.prevent="$event.target.blur()"
                                    />
                                </div>
                                <!-- $0 is allowed (a free size, Jake
                                     2026-09-30) but must never be an
                                     accident, so it is always flagged. A
                                     BLANK size is different: it has no price
                                     and is listed under "Needs a price". -->
                                <span
                                    v-if="isFreeCents(workingPrices[size])"
                                    class="free-chip"
                                    :data-testid="`pricing-fixed-free-${size}`"
                                    title="This size is free to shoppers"
                                >Free</span>
                                <p
                                    v-if="fixedErrors[size]"
                                    class="pricing-error"
                                    :data-testid="`pricing-fixed-error-${size}`"
                                >
                                    {{ fixedErrors[size] }}
                                </p>
                                <span
                                    v-if="fixedRatios(size).belowCost"
                                    class="below-cost-chip"
                                    :data-testid="`pricing-fixed-below-cost-${size}`"
                                    title="Shoppers would pay less than this size costs"
                                >Below cost</span>
                            </td>
                            <td class="layer-cell">
                                <div
                                    class="layer-money"
                                    :data-testid="`pricing-fixed-total-cost-${size}`"
                                >{{ money(fixedCost(size).total) }}</div>
                                <div class="layer-ratio">
                                    <span class="sr-only">Blank cost</span>
                                    <span :data-testid="`pricing-fixed-blank-cost-${size}`">{{ money(fixedCost(size).blank) }}</span>
                                    +
                                    <span class="sr-only">decoration cost</span>
                                    <span :data-testid="`pricing-fixed-deco-cost-${size}`">{{ money(fixedCost(size).decoration) }}</span>
                                </div>
                            </td>
                            <td class="layer-cell">
                                <div
                                    class="layer-money"
                                    :data-testid="`pricing-fixed-margin-${size}`"
                                >{{ formatBasisPoints(fixedRatios(size).marginBp) }}</div>
                                <div class="layer-ratio">
                                    <span :data-testid="`pricing-fixed-markup-${size}`">{{ formatBasisPoints(fixedRatios(size).markupBp) }}</span>
                                    markup
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                </div>

                <p
                    v-if="sizes.length"
                    class="pricing-hint"
                    data-testid="pricing-fixed-costs-hint"
                >
                    Costs are for one item. Decoration usually costs less per
                    item on larger orders, so your margin there is higher.
                </p>

                <p
                    v-if="missingSizes.length"
                    class="pricing-warning"
                    data-testid="pricing-fixed-missing"
                >
                    Needs a price: {{ missingSizes.join(', ') }}
                </p>

                <p
                    v-if="suggestedPrice != null"
                    class="pricing-hint"
                    data-testid="pricing-suggested-price-readout"
                >
                    SanMar suggested retail (XS-XL): {{ formatCurrency(suggestedPrice) }}
                    -- for reference only.
                </p>
            </section>

            <!-- STORE PRICING ---------------------------------------------->
            <section
                v-else-if="displayMode === PRICING_MODE_STORE"
                class="pricing-section"
                data-testid="pricing-store-section"
            >
                <p class="pricing-hint" data-testid="pricing-store-hint">
                    <template v-if="stepperShown">
                        These prices come from this store's pricing tables, for
                        an order of the quantity shown. Use the arrows to see
                        other quantities.
                    </template>
                    <template v-else>
                        These prices come from this store's pricing tables, for
                        one item. Shoppers ordering more may pay less per item.
                    </template>
                </p>
                <p
                    v-if="previewUpdating"
                    class="pricing-hint"
                    data-testid="pricing-preview-updating"
                >
                    Saving -- store prices will appear in a moment.
                </p>

                <div
                    v-if="stepperShown"
                    class="break-stepper"
                    role="group"
                    aria-label="Quantity break"
                    data-testid="pricing-break-stepper"
                >
                    <span class="section-label break-stepper__label">Quantity</span>
                    <button
                        type="button"
                        class="pricing-btn break-stepper__btn"
                        data-testid="pricing-break-down"
                        aria-label="Previous quantity break"
                        :disabled="activeIndex === 0"
                        @click="stepBreak(-1)"
                    >
                        <v-icon icon="keyboard_arrow_down" />
                    </button>
                    <span
                        class="break-stepper__range"
                        data-testid="pricing-break-range"
                        aria-live="polite"
                        :data-qty-min="activeBreak?.qty_min"
                    >{{ activeRangeLabel }}</span>
                    <button
                        type="button"
                        class="pricing-btn break-stepper__btn"
                        data-testid="pricing-break-up"
                        aria-label="Next quantity break"
                        :disabled="activeIndex >= breaks.length - 1"
                        @click="stepBreak(1)"
                    >
                        <v-icon icon="keyboard_arrow_up" />
                    </button>
                    <span class="break-stepper__unit">items</span>
                </div>

                <div v-if="sizes.length" class="price-table-scroll">
                <table class="price-table">
                    <thead>
                        <tr>
                            <th>Size</th>
                            <th>Blank goods <span class="th-sub">cost &rarr; charge</span></th>
                            <th>Decoration <span class="th-sub">cost &rarr; charge</span></th>
                            <th>Store price</th>
                            <th>Cost <span class="th-sub">blank + decoration</span></th>
                            <th>Effective margin <span class="th-sub">markup</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="size in sizes"
                            :key="size"
                            :data-testid="`pricing-preview-row-${size}`"
                        >
                            <td>{{ size }}</td>
                            <td class="layer-cell">
                                <div class="layer-money">
                                    <span class="sr-only">Blank cost</span>
                                    <span :data-testid="`pricing-break-blank-cost-${size}`">{{ money(breakRow(size)?.blank_cost_cents) }}</span>
                                    <span class="layer-arrow" aria-hidden="true">&rarr;</span>
                                    <span class="sr-only">charge</span>
                                    <span :data-testid="`pricing-break-blank-charge-${size}`">{{ money(breakRow(size)?.blank_charge_cents) }}</span>
                                </div>
                                <div class="layer-ratio">
                                    <span :data-testid="`pricing-break-blank-margin-${size}`">{{ formatBasisPoints(breakRow(size)?.blank_margin_bp) }}</span>
                                    margin &middot;
                                    <span :data-testid="`pricing-break-blank-markup-${size}`">{{ formatBasisPoints(breakRow(size)?.blank_markup_bp) }}</span>
                                    markup
                                </div>
                            </td>
                            <td class="layer-cell">
                                <div class="layer-money">
                                    <span class="sr-only">Decoration cost</span>
                                    <span :data-testid="`pricing-break-deco-cost-${size}`">{{ money(breakRow(size)?.decoration_cost_cents) }}</span>
                                    <span class="layer-arrow" aria-hidden="true">&rarr;</span>
                                    <span class="sr-only">charge</span>
                                    <span :data-testid="`pricing-break-deco-charge-${size}`">{{ money(breakRow(size)?.decoration_charge_cents) }}</span>
                                </div>
                                <div class="layer-ratio">
                                    <span :data-testid="`pricing-break-deco-margin-${size}`">{{ formatBasisPoints(breakRow(size)?.decoration_margin_bp) }}</span>
                                    margin &middot;
                                    <span :data-testid="`pricing-break-deco-markup-${size}`">{{ formatBasisPoints(breakRow(size)?.decoration_markup_bp) }}</span>
                                    markup
                                </div>
                            </td>
                            <td :data-testid="`pricing-preview-price-${size}`">
                                <span
                                    v-if="previewUnpriced(size)"
                                    class="preview-unpriced"
                                    :data-testid="`pricing-preview-unpriced-${size}`"
                                >
                                    No supplier cost
                                </span>
                                <template v-else>{{ previewDisplay(size) }}</template>
                                <span
                                    v-if="storeRatios(size).belowCost"
                                    class="below-cost-chip"
                                    :data-testid="`pricing-break-below-cost-${size}`"
                                    title="Shoppers would pay less than this size costs"
                                >Below cost</span>
                            </td>
                            <td class="layer-cell">
                                <div
                                    class="layer-money"
                                    :data-testid="`pricing-break-total-cost-${size}`"
                                >{{ money(storeCost(size).total) }}</div>
                                <div class="layer-ratio">
                                    <span class="sr-only">Blank cost</span>
                                    <span :data-testid="`pricing-break-total-blank-cost-${size}`">{{ money(storeCost(size).blank) }}</span>
                                    +
                                    <span class="sr-only">decoration cost</span>
                                    <span :data-testid="`pricing-break-total-deco-cost-${size}`">{{ money(storeCost(size).decoration) }}</span>
                                </div>
                            </td>
                            <td class="layer-cell">
                                <div
                                    class="layer-money"
                                    :data-testid="`pricing-break-effective-margin-${size}`"
                                >{{ formatBasisPoints(storeRatios(size).marginBp) }}</div>
                                <div class="layer-ratio">
                                    <span :data-testid="`pricing-break-effective-markup-${size}`">{{ formatBasisPoints(storeRatios(size).markupBp) }}</span>
                                    markup
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                </div>

                <button
                    type="button"
                    class="pricing-btn pricing-link"
                    data-testid="pricing-store-link"
                    :disabled="owningStoreId == null"
                    @click="openStorePricing"
                >
                    Edit store pricing tables
                </button>
            </section>
        </v-card-text>

        <DestructiveConfirmDialog
            v-model="confirmStoreOpen"
            title="Switch to store pricing?"
            description="Shoppers will be charged the store pricing tables' prices instead of the fixed prices you set. Your fixed prices are kept, so you can switch back."
            confirm-label="Use store pricing"
            cancel-label="Keep fixed price"
            @confirm="onConfirmStore"
            @cancel="onCancelStore"
        />
    </v-card>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useProductEditorStore } from '@/stores/productEditor'
import { formatCurrency } from '@/utils/currency'
import DestructiveConfirmDialog from '@/pages/distributor/store-editor/StoreProducts/Editor/DestructiveConfirmDialog.vue'
import PricingModeBadge from '@/pages/distributor/store-editor/StoreProducts/PricingModeBadge.vue'
import {
    PRICING_MODE_FIXED,
    PRICING_MODE_STORE,
    normalizePricingMode,
    orderSizes,
    parsePriceToCents,
    centsToInputString,
    centsToDollars,
    missingPriceSizes,
    isFreeCents,
} from '@/pages/distributor/store-editor/StoreProducts/pricingMode'
import {
    qtyRangeLabel,
    formatBasisPoints,
    fixedPriceRatios,
} from '@/pages/distributor/store-editor/StoreProducts/priceBreakdown'
import { supplierLabel } from '@/pages/distributor/store-editor/StoreProducts/supplierLabel'
import {
    sizesNotOffered,
    supplierSizeRun,
} from '@/pages/distributor/store-editor/StoreProducts/supplierSizes'

const store = useProductEditorStore()
const router = useRouter()

const INVALID_PRICE = 'Enter a price of $0.00 or more (dollars and cents).'

const sizes = computed(() =>
    orderSizes(store.getEffectiveAttribute('selected_sizes')),
)

// Selected sizes outside the supplier's run (empty while it is unknown).
const unavailableSizes = computed(() =>
    sizesNotOffered(sizes.value, supplierSizeRun(store.supplierMeta)),
)
const supplierName = computed(
    () =>
        supplierLabel({ supplier_name: store.getEffectiveAttribute('supplier_name') })
        ?? 'the supplier',
)

// The mode the product is actually on (saved or awaiting autosave). The badge
// shows THIS, never a switch still being held for missing prices.
const savedMode = computed(() =>
    normalizePricingMode(store.getEffectiveAttribute('pricing_mode')),
)

// A store -> fixed switch held locally until every size has a price. While
// held, `pendingPrices` is the working map; nothing is written.
const pendingFixed = ref(false)
const pendingPrices = ref({})

const displayMode = computed(() =>
    pendingFixed.value ? PRICING_MODE_FIXED : savedMode.value,
)

const savedFixedPrices = computed(() => {
    const v = store.getEffectiveAttribute('fixed_size_prices')
    return v && typeof v === 'object' ? v : {}
})

const workingPrices = computed(() =>
    pendingFixed.value ? pendingPrices.value : savedFixedPrices.value,
)

const missingSizes = computed(() =>
    missingPriceSizes(sizes.value, workingPrices.value),
)

const pricePreview = computed(() => {
    const v = store.getEffectiveAttribute('price_preview')
    return v && typeof v === 'object' ? v : {}
})

const suggestedPrice = computed(() => {
    const v = store.supplierMeta?.suggested_price
    return typeof v === 'number' && v > 0 ? v : null
})

const serverAttrs = computed(() => store.serverState?.data?.attributes ?? {})

// The preview is the server's quote for the SAVED product. Until a mode
// switch or a size change has been saved, it describes something else, so
// the panel says so instead of showing a stale number.
const previewUpdating = computed(() => {
    if (normalizePricingMode(serverAttrs.value.pricing_mode) !== PRICING_MODE_STORE) {
        return true
    }
    const saved = orderSizes(serverAttrs.value.selected_sizes)
    return saved.join('|') !== sizes.value.join('|')
})

// The store price at the break the stepper is on. Until the breakdown has
// loaded, `price_preview` (quantity 1, i.e. the first break) stands in.
function storePriceCents(size) {
    const row = breakRow(size)
    return row ? row.price_cents : pricePreview.value[size]
}

function previewDisplay(size) {
    if (previewUpdating.value) return formatCurrency(null)
    return money(storePriceCents(size))
}

// A size the CURRENT quote leaves unpriced. In store pricing the server
// prices a size only when it has a supplier cost to mark up (price_for's
// blank_cost_for_size), so the row says that rather than a bare dash. While
// the preview is updating nothing is known yet, so no reason is claimed.
function previewUnpriced(size) {
    if (previewUpdating.value) return false
    const cents = storePriceCents(size)
    return !(Number.isInteger(cents) && cents >= 0)
}

function money(cents) {
    return formatCurrency(centsToDollars(cents))
}

// ---- Per-size breakdown (server-quoted) ----

const priceBreaks = computed(() => store.priceBreaks ?? null)
const breakIndex = ref(0)

// Refetch whenever the saved product's pricing inputs change
// (`priceBreaksKey`: mode, sizes, supplier cost, placements/decorations, the
// product itself). A save that touches none of them -- a fixed price, a
// name -- keeps the breakdown. The stepper returns to the first break, since
// the new quote may have different breaks. The store skips the fetch while
// the product is still loading (null key).
watch(
    () => store.priceBreaksKey,
    () => {
        breakIndex.value = 0
        store.loadPriceBreaks()
    },
    { immediate: true },
)

const priceBreaksFailed = computed(() => store.priceBreaksError != null)

// What the embroidery decoration cost is priced from (Jake, 2026-10-08):
// `{count, source: 'actual' | 'estimate' | null, from_file: bool, status: 'ready' | 'pending'
// | 'unavailable' | 'off'}`, or null when the product has no embroidery. The
// count is the one the server priced with -- the panel only labels it. It can
// be set while the status is not 'ready' (another zone has no count yet).
// 'pending' never promises WHEN: the server cannot see a call-time failure.
const stitchCount = computed(() => {
    const s = priceBreaks.value?.stitch_count
    return s && typeof s === 'object' ? s : null
})

function formatStitches(count) {
    return Number(count).toLocaleString('en-US')
}

// ---- Missing supplier cost: warning, manual entry, takeover ----

const costs = computed(() => {
    const c = priceBreaks.value?.costs
    return c && typeof c === 'object' ? c : {}
})

const manualCostAvailable = computed(
    () => priceBreaks.value?.manual_cost_available === true,
)

const manualCostEditable = computed(
    () => manualCostAvailable.value && priceBreaks.value?.manual_cost_editable === true,
)

// Selected sizes the supplier gave no cost for. Empty until the breakdown
// has loaded -- nothing is claimed about costs that are not known yet.
const missingCostSizes = computed(() =>
    sizes.value.filter((size) => {
        if (unavailableSizes.value.includes(size)) return false
        const c = costs.value[size]
        return c != null && c.supplier_cost_cents == null
    }),
)

// The warning holds sizes still without a cost of the distributor's own;
// sizes priced from an entered cost move to the calm "in use" note.
const missingCostGroups = computed(() => {
    const usingManual = missingCostSizes.value.filter(
        (size) => costs.value[size]?.blank_cost_source === 'manual',
    )
    const uncosted = missingCostSizes.value.filter((size) => !usingManual.includes(size))
    const groups = []
    if (uncosted.length) groups.push({ kind: 'warning', sizes: uncosted })
    if (usingManual.length) groups.push({ kind: 'in-use', sizes: usingManual })
    return groups
})

const takeoverSizes = computed(() =>
    sizes.value.filter((size) => costs.value[size]?.supplier_takeover === true),
)

function costUsedLabel(size) {
    const c = costs.value[size] ?? {}
    if (c.blank_cost_source === 'manual') return `Your cost ${money(c.blank_cost_cents)}`
    if (c.blank_cost_source === 'basis') {
        return `The product's base blank cost ${money(c.blank_cost_cents)}`
    }
    return 'No price -- shoppers cannot order it'
}

const manualCostDrafts = ref({})
const manualCostErrors = ref({})
const manualCostSaving = ref({})
const manualCostSaved = ref({})

const INVALID_COST = 'Enter a cost above $0.00 (dollars and cents).'

function manualCostInputValue(size) {
    if (manualCostDrafts.value[size] !== undefined) return manualCostDrafts.value[size]
    return centsToInputString(costs.value[size]?.manual_cost_cents)
}

function onManualCostInput(size, e) {
    manualCostDrafts.value = { ...manualCostDrafts.value, [size]: e.target.value }
}

function setManualCostError(size, message) {
    const next = { ...manualCostErrors.value }
    if (message) next[size] = message
    else delete next[size]
    manualCostErrors.value = next
}

async function saveManualCost(size) {
    const cents = parsePriceToCents(manualCostInputValue(size))
    if (cents === null || cents <= 0) {
        setManualCostError(size, INVALID_COST)
        return
    }
    setManualCostError(size, null)
    manualCostSaved.value = { ...manualCostSaved.value, [size]: false }
    manualCostSaving.value = { ...manualCostSaving.value, [size]: true }
    try {
        await store.saveManualBlankCost(size, cents)
        manualCostSaved.value = { ...manualCostSaved.value, [size]: true }
        const nextDrafts = { ...manualCostDrafts.value }
        delete nextDrafts[size]
        manualCostDrafts.value = nextDrafts
    } catch (err) {
        setManualCostError(
            size,
            err?.response?.data?.error || 'The cost could not be saved. Try again.',
        )
    } finally {
        manualCostSaving.value = { ...manualCostSaving.value, [size]: false }
    }
}

const takeoverError = ref('')

async function dismissTakeover() {
    takeoverError.value = ''
    try {
        await store.acknowledgeSupplierCosts([...takeoverSizes.value])
    } catch (err) {
        takeoverError.value =
            err?.response?.data?.error || 'That could not be saved. Try again.'
    }
}

const breaks = computed(() => {
    const list = priceBreaks.value?.breaks
    return Array.isArray(list) ? list : []
})

// The stepper's position. Clamped on read, so a re-quote that returns fewer
// breaks lands on the last one instead of showing nothing.
const activeIndex = computed(() =>
    Math.min(breakIndex.value, Math.max(breaks.value.length - 1, 0)),
)
const activeBreak = computed(() => breaks.value[activeIndex.value] ?? null)
const activeRangeLabel = computed(() =>
    activeBreak.value
        ? qtyRangeLabel(activeBreak.value.qty_min, activeBreak.value.qty_max)
        : '',
)

const stepperShown = computed(
    () => sizes.value.length > 0 && breaks.value.length > 0 && !previewUpdating.value,
)

function stepBreak(delta) {
    const next = activeIndex.value + delta
    if (next < 0 || next >= breaks.value.length) return
    breakIndex.value = next
}

// One size at the active break, or null while the quote describes something
// other than what is on screen (an unsaved mode or size change).
function breakRow(size) {
    if (previewUpdating.value) return null
    return activeBreak.value?.sizes?.[size] ?? null
}

// ---- Store pricing: total cost and effective margin at the active break ----

// Total cost of one item at the break the stepper is on: the break row's blank
// cost plus decoration cost. Null parts (no supplier cost, or the quote is not
// of what is on screen) give a null total, shown as a dash.
function storeCost(size) {
    const r = breakRow(size)
    const blank = Number.isInteger(r?.blank_cost_cents) ? r.blank_cost_cents : null
    const decoration = Number.isInteger(r?.decoration_cost_cents)
        ? r.decoration_cost_cents
        : null
    return {
        blank,
        decoration,
        total: blank === null ? null : blank + (decoration ?? 0),
    }
}

// Margin and markup of the DISPLAYED store price over that total cost, by the
// same helper the Fixed table uses. The displayed price (storePriceCents) is
// used rather than the sum of the layer charges so the figure always ties to
// the Store price column. A size the quote leaves unpriced has no price, so
// fixedPriceRatios gives it no ratios and no flag (a dash).
function storeRatios(size) {
    return fixedPriceRatios(storePriceCents(size), storeCost(size).total)
}

// ---- Fixed price: cost and the live margin of the typed price ----

// Costs do not depend on the mode, so they are valid while a switch to
// fixed is still held unsaved.
function fixedCost(size) {
    const c = priceBreaks.value?.costs?.[size]
    const blank = Number.isInteger(c?.blank_cost_cents) ? c.blank_cost_cents : null
    const decoration = Number.isInteger(c?.decoration_cost_cents)
        ? c.decoration_cost_cents
        : null
    return {
        blank,
        decoration,
        total: blank === null ? null : blank + (decoration ?? 0),
    }
}

// The price being considered: the typing in progress if it parses, else the
// working price.
function fixedPriceForRatios(size) {
    const draft = fixedDrafts.value[size]
    if (draft !== undefined) return parsePriceToCents(draft)
    return workingPrices.value[size]
}

function fixedRatios(size) {
    return fixedPriceRatios(fixedPriceForRatios(size), fixedCost(size).total)
}

const owningStoreId = computed(() => {
    const attrs = serverAttrs.value
    const id = attrs.store_id ?? attrs.campaign_id ?? null
    return id == null ? null : id
})

function openStorePricing() {
    if (owningStoreId.value == null) return
    router.push({
        name: 'store_edit',
        params: { id: String(owningStoreId.value), section: 'pricing' },
    })
}

// ---- Fixed price inputs ----

const fixedDrafts = ref({})
const fixedErrors = ref({})
const allSizesDraft = ref('')
const allSizesError = ref('')

function fixedInputValue(size) {
    if (fixedDrafts.value[size] !== undefined) return fixedDrafts.value[size]
    return centsToInputString(workingPrices.value[size])
}

function onFixedInput(size, e) {
    fixedDrafts.value = { ...fixedDrafts.value, [size]: e.target.value }
}

function setError(size, message) {
    const next = { ...fixedErrors.value }
    if (message) next[size] = message
    else delete next[size]
    fixedErrors.value = next
}

function onFixedBlur(size) {
    const draft = fixedDrafts.value[size]
    if (draft === undefined) return
    const cents = parsePriceToCents(draft)
    if (cents === null) {
        // Keep the draft on screen so the person can see what was rejected.
        setError(size, INVALID_PRICE)
        return
    }
    const nextDrafts = { ...fixedDrafts.value }
    delete nextDrafts[size]
    fixedDrafts.value = nextDrafts
    setError(size, null)
    commitPrices({ ...workingPrices.value, [size]: cents })
}

function applyAllSizes() {
    const cents = parsePriceToCents(allSizesDraft.value)
    if (cents === null) {
        allSizesError.value = INVALID_PRICE
        return
    }
    allSizesError.value = ''
    const next = { ...workingPrices.value }
    for (const size of sizes.value) next[size] = cents
    fixedDrafts.value = {}
    fixedErrors.value = {}
    commitPrices(next)
}

// The one write path for fixed prices. A held switch completes here the
// moment the last size is priced, writing prices AND mode together so one
// autosave PATCH carries both.
function commitPrices(next) {
    if (!pendingFixed.value) {
        store.updateField('fixed_size_prices', next)
        return
    }
    pendingPrices.value = next
    if (missingPriceSizes(sizes.value, next).length === 0) {
        store.updateField('fixed_size_prices', next)
        store.updateField('pricing_mode', PRICING_MODE_FIXED)
        pendingFixed.value = false
        pendingPrices.value = {}
    }
}

// ---- Mode switching ----

function chooseFixed() {
    if (displayMode.value === PRICING_MODE_FIXED) return
    // Seed from a previously saved fixed price, else from what the store
    // tables charge today -- so switching alone changes no price. A preview
    // that is still updating describes the previously SAVED product, so it
    // seeds nothing; those sizes ask for a price instead.
    //
    // The quote is the breakdown's FIRST break (quantity 1) when it is
    // loaded: it is re-fetched after a blank cost is entered, while
    // `price_preview` is only refreshed by a product save, so seeding from
    // the preview could persist the price from before the entry.
    const seeded = { ...savedFixedPrices.value }
    const firstBreak = previewUpdating.value ? null : breaks.value[0]?.sizes ?? null
    const preview = previewUpdating.value ? {} : pricePreview.value
    for (const size of sizes.value) {
        if (!Number.isInteger(seeded[size]) || seeded[size] < 0) {
            const quoted = firstBreak ? firstBreak[size]?.price_cents : preview[size]
            if (Number.isInteger(quoted) && quoted >= 0) seeded[size] = quoted
            else delete seeded[size]
        }
    }
    pendingFixed.value = true
    commitPrices(seeded)
}

const confirmStoreOpen = ref(false)

function chooseStore() {
    if (pendingFixed.value) {
        // Nothing was written for the held switch; just drop it.
        pendingFixed.value = false
        pendingPrices.value = {}
        fixedDrafts.value = {}
        fixedErrors.value = {}
        return
    }
    if (displayMode.value === PRICING_MODE_STORE) return
    if (savedMode.value === PRICING_MODE_FIXED) {
        confirmStoreOpen.value = true
        return
    }
    store.updateField('pricing_mode', PRICING_MODE_STORE)
}

function onConfirmStore() {
    confirmStoreOpen.value = false
    fixedDrafts.value = {}
    fixedErrors.value = {}
    store.updateField('pricing_mode', PRICING_MODE_STORE)
}

function onCancelStore() {
    confirmStoreOpen.value = false
}
</script>

<style scoped>
.pricing-header {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
}

.pricing-header-icon {
    flex: 0 0 auto;
    font-size: 28px;
}

.pricing-header-text {
    flex: 1 1 auto;
    min-width: 0;
}

.pricing-title {
    font-weight: 600;
    font-size: 16px;
    line-height: 1.2;
}

.pricing-subtitle {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
    margin-top: 2px;
}

.mode-control {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 16px;
}

.mode-choice {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 12px 14px;
    border: 1px solid var(--tf-slate-border);
    border-radius: 8px;
    background: white;
    cursor: pointer;
    text-align: left;
    font: inherit;
}

.mode-choice--active {
    border: 2px solid var(--tf-slate-ink);
    padding: 11px 13px;
    background: #f6f8fa;
}

.mode-choice__title {
    font-weight: 600;
    font-size: 14px;
}

.mode-choice__copy {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
}

.pricing-section {
    margin-bottom: 8px;
}

.section-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 6px;
}

.same-price-row {
    margin-bottom: 16px;
}

.same-price-controls {
    display: flex;
    align-items: center;
    gap: 12px;
}

.pricing-hint {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
    margin: 0 0 12px;
}

.pricing-warning {
    font-size: 13px;
    color: #8a4b00;
    background: #fff4e0;
    border: 1px solid #f0c27a;
    border-radius: 6px;
    padding: 8px 12px;
    margin: 0 0 12px;
}

.pricing-error {
    font-size: 12px;
    color: #b00020;
    margin: 4px 0 0;
}

.price-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 12px;
}

.price-table th,
.price-table td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid var(--tf-slate-divider);
    font-size: 14px;
    vertical-align: top;
}

.free-chip {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: #8a4b00;
    background: #fff4e0;
    border: 1px solid #f0c27a;
    vertical-align: middle;
}

.cost-alert {
    border-radius: 8px;
    padding: 12px 14px;
    margin: 0 0 16px;
    font-size: 13px;
}

.cost-alert--warning {
    color: #5c2d00;
    background: #fff4e0;
    border: 2px solid #e8a33d;
}

.cost-alert--calm {
    color: #1f3a2b;
    background: #f1f8f4;
    border: 1px solid #9cc9ae;
}

.cost-alert--info {
    color: #0b3a5c;
    background: #eaf4fb;
    border: 2px solid #7fb5dc;
}

.cost-alert__title {
    font-weight: 700;
    font-size: 14px;
    margin: 0 0 6px;
}

.cost-alert__copy {
    margin: 0 0 8px;
}

.cost-alert__saved {
    margin: 4px 0 0;
    font-size: 12px;
    font-weight: 600;
}

.unavailable-list {
    list-style: none;
    padding: 0;
    margin: 8px 0 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.unavailable-list__row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
}

.unavailable-list__row .pricing-btn {
    margin-left: auto;
}

.cost-alert__table {
    border-collapse: collapse;
    margin: 4px 0 8px;
}

.cost-alert__table th,
.cost-alert__table td {
    padding: 4px 12px 4px 0;
    text-align: left;
    vertical-align: top;
}

.cost-alert__table th {
    font-size: 12px;
    font-weight: 600;
}

.below-cost-chip {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: #b00020;
    background: #fdecee;
    border: 1px solid #f2a7b1;
    vertical-align: middle;
}

.price-table-scroll {
    overflow-x: auto;
    margin-bottom: 12px;
}

.price-table-scroll .price-table {
    margin-bottom: 0;
}

.price-table .th-sub {
    display: block;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
}

.layer-cell {
    white-space: nowrap;
}

.layer-money {
    font-variant-numeric: tabular-nums;
}

.layer-arrow {
    margin: 0 4px;
    color: rgba(0, 0, 0, 0.45);
}

.layer-ratio {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    font-variant-numeric: tabular-nums;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.break-stepper {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.break-stepper__label {
    margin-bottom: 0;
}

.break-stepper__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 6px;
}

.break-stepper__range {
    min-width: 64px;
    text-align: center;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.break-stepper__unit {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
}

.preview-unpriced {
    color: #8a4b00;
    font-size: 13px;
}

.price-table th {
    font-weight: 600;
    color: rgba(0, 0, 0, 0.55);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

/* Canonical currency-input shell -- $ prefix is non-editable, sits OUTSIDE
   the input element. The input holds bare numbers only. */
.currency-field {
    display: inline-flex;
    align-items: baseline;
    border: 1px solid var(--tf-slate-border);
    border-radius: 4px;
    overflow: hidden;
    background: white;
    padding-left: 8px;
}

.currency-prefix {
    user-select: none;
}

.price-input {
    width: 90px;
    padding: 4px 8px;
    border: none;
    font: inherit;
    outline: none;
    background: transparent;
}

.pricing-btn {
    background: white;
    border: 1px solid var(--tf-slate-border);
    border-radius: 4px;
    padding: 4px 12px;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.75);
}

.pricing-btn:hover {
    background: #f6f8fa;
}

.pricing-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
